import { Directive, Optional, Host, Self, SkipSelf } from '@angular/core';
import {
  CellClickEvent,
  CellComponent,
  FilterService,
  GridComponent,
} from '@progress/kendo-angular-grid';
import { GridRightClickMenuService } from './grid-right-click-menu.service';

/**
 * A directive that adds right-click context menu functionality to Kendo Grid components.
 * 
 * This directive attaches to Kendo Grid components and registers a cell click event handler
 * that opens a context menu when users right-click on filterable grid cells. The context menu
 * provides options for filtering grid data based on the clicked cell value.
 * 
 * @example
 * ```html
 * <kendo-grid></kendo-grid>
 * <app-grid-context-menu></app-grid-context-menu>
 * ```
 */
@Directive({
  selector: 'kendo-grid',
  standalone: true,
})
export class GridRightClickMenuDirective {
  /**
   * Creates an instance of GridRightClickMenuDirective.
   * @param grid The Kendo grid component this directive is attached to.
   * @param menuService The service that coordinates between the grid, directive and menu.
   * @param filterService The Kendo filter service for applying filters to the grid.
   */
  constructor(
    @Host() @Self() private grid: GridComponent,
    private menuService: GridRightClickMenuService,
    private filterService: FilterService
  ) {
    console.log('GridRightClickMenuDirective');
    grid.cellClick.subscribe(this.onCellClick.bind(this));
  }

  /**
   * Handles cell click events in the grid.
   * Opens the context menu when a right-click (contextmenu) event occurs on a filterable grid.
   * @param e The cell click event from the Kendo grid.
   */
  onCellClick(e: CellClickEvent) {
    console.log('Cell clicked:', e);
    if (e.type === 'contextmenu' && this.grid.filterable) {
      const originalEvent = e.originalEvent;
      originalEvent.preventDefault();

      this.menuService.grid = this.grid;
      this.menuService.event = e;
      this.menuService.filterService = this.filterService;
      this.menuService.menu?.gridContextMenu.show({
        left: originalEvent.pageX,
        top: originalEvent.pageY,
      });
    }
  }
}
